import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, downloadFile } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { FileText, Loader2, Download, Sparkles, Mail, MessageCircle, Share2 } from "lucide-react";
import { formatDateShort } from "@/shared/format";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";

/**
 * "Draft invoice" — BuildIntel drafts a client invoice from a job's approved entries.
 */
export default function DraftInvoiceButton({ job }) {
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markup, setMarkup] = useState(20);
  const [includePending, setIncludePending] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);
  // Reuse the same share primitive as Accountant Pack / BAS / TPAR so
  // "Email" and "Share" attach the real PDF (Web Share files on mobile,
  // auto-download + mailto: on desktop). accountantEmail is deliberately
  // NOT used here — the mailto: opens with an empty To: so the tradie can
  // paste the client's address without our helper misrouting it.
  const { bizName, abn, shareVia: shareToAccountant } = useShareToAccountant();

  const draft = async () => {
    setLoading(true);
    setInvoice(null);
    try {
      const { data } = await api.post("/ai/draft-invoice", {
        job_id: job.id, markup_pct: Number(markup), include_pending: includePending,
      });
      setInvoice(data);
    } catch (e) {
      devLog.warn("draft-invoice failed", e);
      const detail = e.response?.data?.detail || "Couldn't draft an invoice";
      toast.error(typeof detail === "string" ? detail : "Draft failed");
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => { setOpen(true); if (!invoice && !loading) draft(); };

  const buildShareText = () => {
    if (!invoice) return "";
    const bizLine = bizName ? `${bizName}${abn ? ` · ABN ${abn}` : ""}\n` : "";
    const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      `Hi,\n\n` +
      `Please find attached invoice ${invoice.invoice_number} for ${job.name}.\n\n` +
      `${bizLine}` +
      `Total due: ${fmt(invoice.total)}\n` +
      `Issued: ${formatDateShort(invoice.issue_date)}\n` +
      `Due: ${formatDateShort(invoice.due_date)} (within 14 days)\n\n` +
      `Full invoice PDF attached (prepared with BuildIntel AI — confirm figures before sending).`
    );
  };

  // First tap on Download / WhatsApp / Email / Share opens the in-app
  // preview sheet. Share happens from inside the preview via the OS
  // share sheet — never on first tap.
  const openInvoicePreview = () => {
    if (!invoice) return;
    setPreviewCfg({
      pdfUrl: `/ai/invoice/${invoice.id}/pdf`,
      filename: `invoice-${invoice.invoice_number}.pdf`,
      title: `Invoice ${invoice.invoice_number}`,
      description: "Review the invoice before you Share",
      shareText: buildShareText(),
      shareSubject: `${bizName || "Invoice"} — ${invoice.invoice_number}`,
    });
    setPreviewOpen(true);
  };
  const downloadPdf = openInvoicePreview;
  // eslint-disable-next-line no-unused-vars
  const _legacyDownload = async () => {
    if (!invoice) return;
    setDlBusy(true);
    try {
      await downloadFile(
        `/api/ai/invoice/${invoice.id}/pdf`,
        `invoice-${invoice.invoice_number}.pdf`
      );
      toast.success("Invoice PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDlBusy(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const share = async (_kind) => {
    if (!invoice) return;
    openInvoicePreview();
  };

  return (
    <>
      <button
        type="button"
        data-testid={`draft-invoice-btn-${job.id}`}
        onClick={onOpen}
        className="h-10 px-3 rounded-lg bi-ghost-btn text-xs inline-flex items-center gap-1.5 hover:border-[#FF9500]/40 transition"
      >
        <FileText className="w-3.5 h-3.5 text-[#FF9500]" /> Draft invoice
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#1F1F1F] border-white/10 text-white p-0 max-h-[90vh] overflow-y-auto">
          <SheetHeader className="px-5 pt-5 pb-2 text-left">
            <SheetTitle className="font-heading font-extrabold text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF9500]" /> Draft invoice
            </SheetTitle>
            <SheetDescription className="text-white/60 text-sm">
              BuildIntel drafts a simple client invoice from {job.name}&rsquo;s approved entries. Review before sending.
            </SheetDescription>
          </SheetHeader>
          <div className="px-5 pt-3 pb-6 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <label className="bi-surface-2 rounded-xl px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-white/50">Markup %</div>
                <input
                  type="number"
                  min="0"
                  max="200"
                  value={markup}
                  onChange={(e) => setMarkup(e.target.value)}
                  className="mt-1 w-full bg-transparent text-white outline-none font-heading font-extrabold text-xl"
                  data-testid="draft-markup"
                />
              </label>
              <label className="bi-surface-2 rounded-xl px-3 py-2 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={includePending}
                  onChange={(e) => setIncludePending(e.target.checked)}
                  className="w-5 h-5 accent-[#FF9500]"
                  data-testid="draft-include-pending"
                />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Include pending</div>
                  <div className="text-xs text-white/70">Uses all non-rejected entries</div>
                </div>
              </label>
            </div>
            <button
              type="button"
              onClick={draft}
              disabled={loading}
              data-testid="draft-invoice-generate"
              className="w-full h-12 rounded-xl bi-ghost-btn flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#FF9500]" />}
              {loading ? "Drafting your invoice…" : "Regenerate"}
            </button>

            {invoice && (
              <div className="bi-surface p-4 space-y-3" data-testid="invoice-preview">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading font-extrabold text-lg">{invoice.invoice_number}</div>
                    <div className="text-xs text-white/60">Issued {formatDateShort(invoice.issue_date)} · Due {formatDateShort(invoice.due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Total due</div>
                    <div className="font-heading font-extrabold text-2xl text-[#FF9500]">${Number(invoice.total || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Bill to</div>
                  <div>{invoice.bill_to}</div>
                </div>
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-white/50">Line items</div>
                  {(invoice.line_items || []).map((li, i) => (
                    <div
                      key={li.id || `${li.description || "line"}-${i}`}
                      className="flex justify-between gap-3 text-sm"
                    >
                      <div className="text-white/85 flex-1">{li.description}</div>
                      <div className="text-white/90 font-medium shrink-0">${Number(li.amount || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-2 text-sm space-y-1">
                  <div className="flex justify-between text-white/60"><span>Subtotal</span><span>${Number(invoice.subtotal || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between text-white/60"><span>GST (10%)</span><span>${Number(invoice.gst || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between font-heading font-extrabold text-white"><span>Total</span><span className="text-[#FF9500]">${Number(invoice.total || 0).toFixed(2)}</span></div>
                </div>
                {invoice.notes && (
                  <p className="text-xs text-white/60 italic border-t border-white/10 pt-2">{invoice.notes}</p>
                )}
                <button
                  type="button"
                  onClick={downloadPdf}
                  disabled={dlBusy || shareBusy}
                  data-testid="invoice-download-pdf"
                  className="w-full h-12 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
                >
                  {dlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {dlBusy ? "Building PDF…" : "Download PDF"}
                </button>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => share("whatsapp")}
                    disabled={shareBusy}
                    data-testid="invoice-share-whatsapp"
                    className="h-11 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" /> WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => share("email")}
                    disabled={shareBusy}
                    data-testid="invoice-share-email"
                    className="h-11 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#FF9500]" /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => share("native")}
                    disabled={shareBusy}
                    data-testid="invoice-share-native"
                    className="h-11 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Share2 className="w-3.5 h-3.5 text-white/70" /> Share
                  </button>
                </div>
                <p className="text-[10px] text-white/40 text-center">Prepared with BuildIntel AI — confirm figures before sending.</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <PdfPreviewSheet
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
        pdfUrl={previewCfg?.pdfUrl}
        filename={previewCfg?.filename}
        title={previewCfg?.title}
        description={previewCfg?.description}
        shareText={previewCfg?.shareText}
        shareSubject={previewCfg?.shareSubject}
        testid={`invoice-preview-${job.id}`}
      />
    </>
  );
}
